// England and Wales dates published at https://www.gov.uk/bank-holidays
const UK_BANK_HOLIDAYS = new Set([
  '2026-01-01',
  '2026-04-03',
  '2026-04-06',
  '2026-05-04',
  '2026-05-25',
  '2026-08-31',
  '2026-12-25',
  '2026-12-28',
  '2027-01-01',
  '2027-03-26',
  '2027-03-29',
  '2027-05-03',
  '2027-05-31',
  '2027-08-30',
  '2027-12-27',
  '2027-12-28',
  '2028-01-03',
  '2028-04-14',
  '2028-04-17',
  '2028-05-01',
  '2028-05-29',
  '2028-08-28',
  '2028-12-25',
  '2028-12-26',
]);

class ProductDeliveryEstimate extends HTMLElement {
  connectedCallback() {
    this.cutoffHour = Number(this.dataset.cutoffHour || 14);
    this.deliveryBusinessDays = Number(this.dataset.deliveryBusinessDays || 2);
    this.updateEstimate();
    window.requestAnimationFrame(() => {
      this.dataset.deliveryReady = 'true';
    });
    this.updateTimer = window.setInterval(() => this.updateEstimate(), 30000);
  }

  disconnectedCallback() {
    window.clearInterval(this.updateTimer);
  }

  londonDateParts(date) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);

    return Object.fromEntries(
      parts
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, Number(part.value)]),
    );
  }

  londonTimeToInstant(year, month, day, hour) {
    const utcGuess = new Date(Date.UTC(year, month - 1, day, hour));
    const londonGuess = this.londonDateParts(utcGuess);
    const londonAsUtc = Date.UTC(
      londonGuess.year,
      londonGuess.month - 1,
      londonGuess.day,
      londonGuess.hour,
      londonGuess.minute,
      londonGuess.second,
    );
    const offset = londonAsUtc - utcGuess.getTime();

    return new Date(utcGuess.getTime() - offset);
  }

  isUKBankHoliday(date) {
    const dateKey = [
      date.getUTCFullYear(),
      String(date.getUTCMonth() + 1).padStart(2, '0'),
      String(date.getUTCDate()).padStart(2, '0'),
    ].join('-');

    return UK_BANK_HOLIDAYS.has(dateKey);
  }

  isProcessingDay(date) {
    const day = date.getUTCDay();

    return day !== 0 && day !== 6 && !this.isUKBankHoliday(date);
  }

  isDeliveryDay(date) {
    return date.getUTCDay() !== 0 && !this.isUKBankHoliday(date);
  }

  nextProcessingDay(date) {
    const nextDate = new Date(date.getTime());

    do {
      nextDate.setUTCDate(nextDate.getUTCDate() + 1);
    } while (!this.isProcessingDay(nextDate));

    return nextDate;
  }

  nextDeliveryDay(date) {
    const nextDate = new Date(date.getTime());

    do {
      nextDate.setUTCDate(nextDate.getUTCDate() + 1);
    } while (!this.isDeliveryDay(nextDate));

    return nextDate;
  }

  addDeliveryDays(date, days) {
    let result = new Date(date.getTime());

    for (let day = 0; day < days; day += 1) {
      result = this.nextDeliveryDay(result);
    }

    return result;
  }

  weekday(date) {
    return new Intl.DateTimeFormat(document.documentElement.lang || 'en-GB', {
      weekday: 'long',
      timeZone: 'UTC',
    }).format(date);
  }

  countdown(milliseconds) {
    const totalMinutes = Math.max(1, Math.ceil(milliseconds / 60000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const parts = [];

    if (hours > 0) {
      parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
    }

    if (minutes > 0) {
      parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
    }

    return parts.join(' and ');
  }

  updateEstimate() {
    const now = new Date();
    const londonNow = this.londonDateParts(now);
    const londonToday = new Date(Date.UTC(
      londonNow.year,
      londonNow.month - 1,
      londonNow.day,
    ));
    const cutoff = this.londonTimeToInstant(
      londonNow.year,
      londonNow.month,
      londonNow.day,
      this.cutoffHour,
    );
    const isBeforeCutoff = this.isProcessingDay(londonToday) && now < cutoff;
    const dispatchDate = isBeforeCutoff ? londonToday : this.nextProcessingDay(londonToday);
    const arrivalDate = this.addDeliveryDays(dispatchDate, this.deliveryBusinessDays);
    const calendarTomorrow = new Date(londonToday.getTime());
    calendarTomorrow.setUTCDate(calendarTomorrow.getUTCDate() + 1);
    const arrivesTomorrow =
      this.deliveryBusinessDays === 1 &&
      arrivalDate.getTime() === calendarTomorrow.getTime();
    const dispatchText = arrivesTomorrow
      ? 'Arrives tomorrow'
      : `Arrives on ${this.weekday(arrivalDate)}`;
    const detailText = isBeforeCutoff
      ? `order within ${this.countdown(cutoff - now)}`
      : `order now for processing ${this.weekday(dispatchDate)}`;

    this.querySelectorAll('[data-delivery-dispatch]').forEach((element) => {
      element.textContent = dispatchText;
    });

    this.querySelectorAll('[data-delivery-countdown]').forEach((element) => {
      element.textContent = detailText;
    });
  }
}

if (!customElements.get('product-delivery-estimate')) {
  customElements.define('product-delivery-estimate', ProductDeliveryEstimate);
}
